import React, { useEffect, useState } from 'react';
import {
    Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField,
} from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { MyBreakdownService } from '../../services/maintenance.service';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import { EquipmentLookupDTO, LocationLookupDTO, MyBreakdownRequestView } from '../../interfaces/maintenance';

interface Props {
    open: boolean;
    onClose: () => void;
    onCreated: (r: MyBreakdownRequestView) => void;
    /** Machine to preselect (opened from its QR sticker). */
    initialEquipmentId?: string;
    /** Full machine record when the caller already has it (no lookup needed). */
    initialEquipment?: EquipmentLookupDTO | null;
}

/** Any employee can report a breakdown: pick the machine, or a location when the machine is not in the register. */
const ReportBreakdownDialog: React.FC<Props> = ({ open, onClose, onCreated, initialEquipmentId, initialEquipment }) => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [equipment, setEquipment] = useState<EquipmentLookupDTO[]>([]);
    const [locations, setLocations] = useState<LocationLookupDTO[]>([]);
    const [equipmentId, setEquipmentId] = useState('');
    const [locationId, setLocationId] = useState('');
    const [pickedEquipment, setPickedEquipment] = useState<EquipmentLookupDTO | null>(null);
    const [pickedLocation, setPickedLocation] = useState<LocationLookupDTO | null>(null);
    const [stopped, setStopped] = useState(false);
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setEquipmentId(''); setLocationId(''); setStopped(false); setDescription('');
        setPickedEquipment(null); setPickedLocation(null);
        const preset = initialEquipment ?? null;
        if (preset) {
            setEquipment([preset]); setEquipmentId(preset.id); setPickedEquipment(preset);
        } else if (initialEquipmentId) {
            MyBreakdownService.lookupEquipment(institutionId, '', initialEquipmentId).then(list => {
                setEquipment(list);
                if (list[0]) { setEquipmentId(list[0].id); setPickedEquipment(list[0]); }
            }).catch(() => setEquipment([]));
        } else {
            MyBreakdownService.lookupEquipment(institutionId, '').then(setEquipment).catch(() => setEquipment([]));
        }
        MyBreakdownService.lookupLocations(institutionId).then(setLocations).catch(() => setLocations([]));
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const searchEquipment = (q: string) => {
        MyBreakdownService.lookupEquipment(institutionId, q).then(setEquipment).catch(() => undefined);
    };

    const valid = description.trim().length > 0 && (equipmentId !== '' || locationId !== '');

    const submit = async () => {
        try {
            setSaving(true);
            const created = await MyBreakdownService.raise({
                institutionId,
                equipmentId: equipmentId || undefined,
                locationDepartmentId: equipmentId ? undefined : locationId || undefined,
                machineStopped: stopped,
                stoppedAt: stopped ? new Date().toISOString() : undefined,
                description: description.trim(),
            });
            toast.showSuccess(`Breakdown ${created.requestNo} reported`);
            onCreated(created);
            onClose();
        } catch (e) {
            toast.showApiError(e, 'Failed to report the breakdown');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'maint.reportBreakdown.page' } as any}>
            <DialogTitle>Report a breakdown</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <SearchableSelect
                        options={equipment} value={equipmentId} label="Machine / equipment" data-help="maint.reportBreakdown.field.equipment" size="small"
                        helperText={equipment.length >= 20 ? 'Showing the first 20 machines. Type a code or name to find others.' : 'Pick a machine, or type a code or name to search.'}
                        getOptionLabel={e => `${e.code} - ${e.name}${e.locationName ? ` (${e.locationName})` : ''}`}
                        selectedOption={pickedEquipment}
                        disableClientFilter onInputChange={searchEquipment}
                        onChange={id => { setEquipmentId(id); setPickedEquipment(equipment.find(e => e.id === id) ?? null); }}
                    />
                    {!equipmentId && (
                        <SearchableSelect
                            options={locations} value={locationId} label="Location (if the machine is not listed)" data-help="maint.reportBreakdown.field.location" size="small"
                            getOptionLabel={l => l.name}
                            selectedOption={pickedLocation}
                            onChange={id => { setLocationId(id); setPickedLocation(locations.find(l => l.id === id) ?? null); }}
                        />
                    )}
                    <FormControlLabel data-help="maint.reportBreakdown.field.stopped"
                        control={<Checkbox checked={stopped} onChange={e => setStopped(e.target.checked)} />}
                        label="The machine has stopped"
                    />
                    <TextField
                        label="What is wrong?" value={description} multiline minRows={3} required data-help="maint.reportBreakdown.field.description" size="small"
                        inputProps={{ maxLength: 1000 }}
                        onChange={e => setDescription(e.target.value)}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} data-help="maint.reportBreakdown.cancel">Cancel</Button>
                <Button variant="contained" disabled={!valid || saving} onClick={submit} data-help="maint.reportBreakdown.submit">Report</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReportBreakdownDialog;
